"use client";

import {
  STATSIG_EVENTS,
  useRoutePathname,
  useStatsigClient,
  type RegisteredStatsigEvent,
} from "@repo/statsig";
import { VaulModal } from "@repo/ui";
import { getNpsThemeClasses } from "@repo/nps/branded";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import useMeasure from "react-use-measure";
import { twMerge } from "tailwind-merge";
import { Drawer } from "vaul";
import { ANIMATION, NPS_STAGES } from "../constants";
import { getScoreCategory } from "../score";
import type { NpsBrandConfig, NpsCloseReason, NpsStage } from "../types";
import { FollowUp } from "./follow-up";
import { ScoreSelection } from "./score-selection";
import { ThankYou } from "./thank-you";

interface NpsDrawerProps {
  isOpen: boolean;
  onClose: (reason?: NpsCloseReason) => void;
  /** Brand-resolved survey config, supplied by the consuming app. */
  config: NpsBrandConfig;
  /** Auto-dismiss when the user leaves the tab, treated as "Not now" at score
   *  selection and score-only at follow-up. Defaults off. */
  dismissOnTabSwitch?: boolean;
}

export function NpsDrawer({
  isOpen,
  onClose,
  config,
  dismissOnTabSwitch = false,
}: NpsDrawerProps) {
  const [stage, setStage] = useState<NpsStage>(NPS_STAGES.SCORE_SELECTION);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [elementRef, bounds] = useMeasure({
    debounce: { scroll: 0, resize: 0 },
  });
  const previousHeightRef = useRef<number | undefined>(undefined);

  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();
  const themeClasses = getNpsThemeClasses();
  // When the user (or a test harness) prefers reduced motion, collapse the
  // height/crossfade animations to instant. Keeps the stage transitions
  // accessible and lets visual snapshots capture a settled, stable layout.
  const prefersReducedMotion = useReducedMotion();

  // Reset state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setStage(NPS_STAGES.SCORE_SELECTION);
      setSelectedScore(null);
      previousHeightRef.current = undefined;
    }
  }, [isOpen]);

  // Auto-dismiss thank you stage after delay. A score was submitted, so this
  // counts as a response (full cooldown), not a deferral.
  useEffect(() => {
    if (stage === NPS_STAGES.THANK_YOU && isOpen) {
      const timer = setTimeout(() => {
        onClose("responded");
      }, ANIMATION.AUTO_DISMISS_DELAY_MS);

      return () => clearTimeout(timer);
    }
  }, [stage, isOpen, onClose]);

  // Calculate dynamic opacity duration based on height change
  const opacityDuration = useMemo(() => {
    const currentHeight = bounds.height;
    const previousHeight = previousHeightRef.current ?? 0;

    if (previousHeightRef.current === undefined) {
      previousHeightRef.current = currentHeight;
      return ANIMATION.MIN_DURATION;
    }

    const heightDifference = Math.abs(currentHeight - previousHeight);
    previousHeightRef.current = currentHeight;

    const duration = Math.min(
      Math.max(
        heightDifference / ANIMATION.HEIGHT_DIFFERENCE_DIVISOR,
        ANIMATION.MIN_DURATION
      ),
      ANIMATION.MAX_DURATION
    );

    return duration;
  }, [bounds.height]);

  const heightDuration = prefersReducedMotion
    ? 0
    : ANIMATION.HEIGHT_TRANSITION_DURATION;
  const crossfadeDuration = prefersReducedMotion ? 0 : opacityDuration;

  const handleScoreSelect = (score: number) => {
    setSelectedScore(score);
  };

  const handleScoreSubmit = () => {
    // Only advance to follow-up when explicitly clicking the Submit CTA
    if (selectedScore === null) {
      return;
    }
    setStage(NPS_STAGES.FOLLOW_UP);
  };

  const handleNotNow = () => {
    // No event logged. "Not now" is a soft decline -> deferral, not cooldown.
    onClose("deferred");
  };

  const handleFollowUpSend = (message: string, contactConsent: boolean) => {
    if (selectedScore === null) {
      return;
    }

    // Log NPS_Completed event with score and follow-up
    logNpsEvent(selectedScore, message, contactConsent);

    setStage(NPS_STAGES.THANK_YOU);
  };

  const handleFollowUpSkip = () => {
    if (selectedScore === null) {
      return;
    }

    // Log NPS_Completed event with score only (no follow-up)
    logNpsEvent(selectedScore, "", false);

    // A score was submitted -> response, full cooldown.
    onClose("responded");
  };

  const handleDismiss = () => {
    // Dismissing before picking a score is a soft decline -> deferral.
    if (stage === NPS_STAGES.SCORE_SELECTION) {
      onClose("deferred");
      return;
    }

    // If user dismisses at follow-up, log event with score only
    if (stage === NPS_STAGES.FOLLOW_UP && selectedScore !== null) {
      logNpsEvent(selectedScore, "", false);
    }

    // A score was submitted before dismissing -> response, full cooldown.
    onClose("responded");
  };

  // Keep a live ref to handleDismiss so the tab-switch listener (bound once per
  // open) always runs the current-stage logic without re-binding each render.
  const handleDismissRef = useRef(handleDismiss);
  useEffect(() => {
    handleDismissRef.current = handleDismiss;
  });

  // Some brands auto-dismiss the survey when the user leaves the tab (per
  // their PRD). We key off document visibility; handleDismiss already maps the
  // stage to the right outcome (score selection -> "Not now" deferral,
  // follow-up -> score-only). Gated by dismissOnTabSwitch so brands that never
  // pass it are unaffected.
  useEffect(() => {
    if (!isOpen || !dismissOnTabSwitch) {
      return;
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        handleDismissRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isOpen, dismissOnTabSwitch]);

  const logNpsEvent = (
    score: number,
    message: string,
    contactConsent: boolean
  ) => {
    const category = getScoreCategory(score);
    const hasFollowUp = message.trim().length > 0 ? "yes" : "no";

    const statsigEvent: RegisteredStatsigEvent = {
      eventName: STATSIG_EVENTS.NPS_COMPLETED,
      value: category,
      metadata: {
        ...locationData,
        hasFollowUp,
        FollowUpMessage: message.trim() || undefined,
        canContact: contactConsent.toString(),
        score: score.toString(),
      },
    };

    logEvent(statsigEvent);
  };

  const renderStageContent = () => {
    switch (stage) {
      case NPS_STAGES.SCORE_SELECTION:
        return (
          <ScoreSelection
            copy={config.copy}
            selectedScore={selectedScore}
            onScoreSelect={handleScoreSelect}
            onSubmit={handleScoreSubmit}
            onNotNow={handleNotNow}
          />
        );
      case NPS_STAGES.FOLLOW_UP:
        if (selectedScore === null) {
          return null;
        }
        return (
          <FollowUp
            copy={config.copy}
            score={selectedScore}
            onSend={handleFollowUpSend}
            onSkip={handleFollowUpSkip}
          />
        );
      case NPS_STAGES.THANK_YOU:
        return (
          <ThankYou
            copy={config.copy}
            thankYouIllustration={config.thankYouIllustration}
          />
        );
      default:
        return null;
    }
  };

  // Custom drawer content styling to match our design (16px side margins, 40px bottom)
  const drawerContentClassname =
    "fixed bottom-[40px] left-[16px] right-[16px] z-[100000] outline-none flex justify-center w-[calc(100%-32px)] max-h-[calc(100vh-80px)]";

  const cardClassName = twMerge(
    "w-full overflow-hidden p-0",
    themeClasses.container
  );

  return (
    <VaulModal
      isOpen={isOpen}
      onClose={handleDismiss}
      dismissible={false}
      drawerContentClassname={drawerContentClassname}
      cardClassName={cardClassName}
      repositionInputs={false}
      fixed={true}
    >
      <Drawer.Title className="sr-only">Net Promoter Score Survey</Drawer.Title>
      <Drawer.Description className="sr-only">
        Rate your likelihood to recommend {config.copy.productName} and provide
        feedback
      </Drawer.Description>

      <motion.div
        animate={{
          height: bounds.height || "auto",
          transition: {
            duration: heightDuration,
            ease: [0.25, 1, 0.5, 1],
          },
        }}
        className="overflow-hidden w-full flex items-center justify-center"
      >
        <div
          ref={elementRef}
          className="flex flex-col gap-[24px] items-center p-[24px] pb-[24px] w-full"
        >
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              key={stage}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{
                duration: crossfadeDuration,
                ease: [0.26, 0.08, 0.25, 1],
              }}
            >
              {renderStageContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </VaulModal>
  );
}
